// LocalStorage key for track order
const TRACK_ORDER_KEY = "hk_track_add_order";

// Interface for track order entry with timestamp
interface TrackOrderEntry {
  trackKey: string;
  addedAt: string; // ISO date string
}

/**
 * Get the raw track order data from localStorage
 * Returns an array of track order entries with timestamps
 */
export const getTrackOrderData = (): TrackOrderEntry[] => {
  try {
    const stored = localStorage.getItem(TRACK_ORDER_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Handle migration from old format (string array) to new format (object array)
      if (Array.isArray(parsed) && parsed.length > 0) {
        if (typeof parsed[0] === "string") {
          // Old format - migrate to new format
          return parsed.map((trackKey: string) => ({
            trackKey,
            addedAt: new Date().toISOString(), // Use current time for migrated entries
          }));
        }
        return parsed;
      }
    }
  } catch (error) {
    console.error("Error reading track order from localStorage:", error);
  }
  return [];
};

/**
 * Get the current track order from localStorage
 * Returns an array of track keys in the order they were added
 */
export const getTrackOrder = (): string[] => {
  return getTrackOrderData().map((entry) => entry.trackKey);
};

/**
 * Get track order with timestamps for validation
 * Returns an array of objects with trackKey and addedAt timestamp
 */
export const getTrackOrderWithTimestamps = (): TrackOrderEntry[] => {
  return getTrackOrderData();
};

/**
 * Add a track to the order (if not already present)
 * New tracks are appended to the end of the order with current timestamp
 */
export const addTrackToOrder = (trackKey: string): void => {
  try {
    const currentOrder = getTrackOrderData();
    const exists = currentOrder.some((entry) => entry.trackKey === trackKey);
    
    if (!exists) {
      currentOrder.push({
        trackKey,
        addedAt: new Date().toISOString(),
      });
      localStorage.setItem(TRACK_ORDER_KEY, JSON.stringify(currentOrder));
    }
  } catch (error) {
    console.error("Error saving track order to localStorage:", error);
  }
};

/**
 * Remove a track from the order (when all sessions for that track are removed)
 */
export const removeTrackFromOrder = (trackKey: string): void => {
  try {
    const currentOrder = getTrackOrderData();
    const newOrder = currentOrder.filter((entry) => entry.trackKey !== trackKey);
    localStorage.setItem(TRACK_ORDER_KEY, JSON.stringify(newOrder));
  } catch (error) {
    console.error("Error removing track from order in localStorage:", error);
  }
};

/**
 * Clear the entire track order (e.g., when cart is emptied)
 */
export const clearTrackOrder = (): void => {
  try {
    localStorage.removeItem(TRACK_ORDER_KEY);
  } catch (error) {
    console.error("Error clearing track order from localStorage:", error);
  }
};

/**
 * Sync track order with current tracks in cart
 * ONLY removes tracks that are no longer in cart, preserves order for remaining tracks
 * Does NOT add new tracks - that should be done by addTrackToOrder when tracks are first added
 */
export const syncTrackOrderWithCart = (currentTrackKeys: string[]): void => {
  try {
    const storedOrder = getTrackOrderData();
    
    // Only keep tracks that are still in cart, maintaining their original order and timestamps
    // Do NOT add new tracks here - they should be added via addTrackToOrder
    const syncedOrder = storedOrder.filter((entry) => currentTrackKeys.includes(entry.trackKey));
    
    localStorage.setItem(TRACK_ORDER_KEY, JSON.stringify(syncedOrder));
  } catch (error) {
    console.error("Error syncing track order in localStorage:", error);
  }
};
